/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import React from 'react'
import { IStatusAlerts, IAdditionalPayer, IAdditionalIdentifiers, IStatusAlertInsuranceType, ICoverageDiscoveryResponseStateManager } from '../../../../services/CoverageDiscoveryResponseStateManager/contracts'
import './StatusAlertSection.scss'
import moment from 'moment'
import NumberFormat from 'react-number-format'
import { observer } from 'mobx-react'
import { CommonUtils } from '../../../../utils/CommonUtils'

export interface IStatusAlertSectionProps {
	CoverageDiscoveryResponseStateManager: ICoverageDiscoveryResponseStateManager
}
interface IStatusAlertSectionState {
	IsPageLoading: boolean
}

@observer
export class StatusAlertSection extends React.Component<IStatusAlertSectionProps, IStatusAlertSectionState> {
	constructor(props: Readonly<IStatusAlertSectionProps>) {
		super(props)
		this.state = {
			IsPageLoading: true,
		}
	}

	componentDidMount(): void {
		this.setIsPageLoadState(false)
	}
	setIsPageLoadState(val: boolean) {
		this.setState({
			IsPageLoading: val,
		})
	}

	GetInsuranceTypeCodesByAlertType(type: string): string[] {
		let codes: string[] = []
		switch (type) {
			case 'MedicareAdvantage':
				codes = ['HM', 'HN', 'IN', 'PR', 'PS']
				break
			case 'Medicaid':
				codes = ['MC']
				break
			case 'PartD':
				codes = ['OT']
				break
			case 'MedicareSecondaryPayer':
				codes = ['12', '13', '14', '15', '16', '41', '42', '43', '47', 'WC']
				break
			case 'SupplementalPayer':
				codes = ['MH', 'MI', 'SP']
				break
			case 'QualifiedMedicareBeneficiary':
				codes = ['QM']
				break
			default:
				break
		}
		return codes
	}
	groupBy(array: IAdditionalPayer[], f) {
		const groups = {}
		array.forEach(function(o) {
			const group = JSON.stringify(f(o))
			groups[group] = groups[group] || []
			groups[group].push(o)
		})
		return Object.keys(groups).map(function(group) {
			return groups[group]
		})
	}

	renderStatusAlert(statusAlerts: IStatusAlerts) {
		let additionalPayers: Array<IAdditionalPayer> = []

		return statusAlerts.insurances.map(insurance => {
			const InsuranceTypeCodes = this.GetInsuranceTypeCodesByAlertType('MedicareAdvantage')
			if (InsuranceTypeCodes.indexOf(insurance.code) > -1) {
				const groupedAdditionalPayers = this.groupBy(insurance.additionalPayers, function(additionalPayers) {
					return [additionalPayers.startDate, additionalPayers.endDate, additionalPayers.message, additionalPayers.insuranceTypeCodeDefinition, additionalPayers.name, additionalPayers.address1, additionalPayers.address2, additionalPayers.city, additionalPayers.state, additionalPayers.zip, additionalPayers.telephone]
				})
				additionalPayers = groupedAdditionalPayers.map(x => x[0])
			} else {
				additionalPayers = insurance.additionalPayers
			}

			return additionalPayers.map((payer, index) => {
				const statusAlertClass = insurance.type === IStatusAlertInsuranceType.PartD || insurance.type === IStatusAlertInsuranceType.QMB ? 'response-table service-types-table' : 'response-table service-types-table status-alert'
				return (
					<table key={index} className={statusAlertClass} style={{ width: '100%' }}>
						<thead>
							<tr>
								<td style={{ paddingLeft: '10px' }} colSpan={4}>
									<h4>
										{insurance.type === IStatusAlertInsuranceType.PartD || insurance.type === IStatusAlertInsuranceType.QMB ? '' : 'Status Alert:'} {insurance.typeString}
									</h4>
								</td>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td>Enrollment:</td>
								{moment(payer.startDate).format('MM/DD/YYYY') === moment(payer.endDate).format('MM/DD/YYYY') && moment(payer.startDate).format('MM/DD/YYYY') !== moment('1/1/0001 12:00:00 AM').format('MM/DD/YYYY') && moment(payer.endDate).format('MM/DD/YYYY') !== moment('1/1/0001 12:00:00 AM').format('MM/DD/YYYY') ? <td className="bottom-border-td">{CommonUtils.DateRange(payer.startDate, payer.endDate)}</td> : <td className="bottom-border-td">{CommonUtils.DateRange(payer.startDate, payer.endDate)}</td>}
								<td className="bottom-border-td">Contract / Plan #:</td>
								{insurance.type === IStatusAlertInsuranceType.PartD || (!(payer.message === null || payer.message === '') && (payer.message.toUpperCase().indexOf('MCO BILL OPTION CODE') > -1 || payer.message.toUpperCase().indexOf('MA BILL OPTION CODE') > -1)) ? (
									<td className="bottom-border-td">{this.getIdentifier(payer.additionalIdentifiers)}</td>
								) : (
									<td className="bottom-border-td">
										{payer.additionalIdentifiers
											.map(i => i.identifier)
											.slice(0, 1)
											.shift()}
									</td>
								)}
							</tr>
							<tr>
								<td>Plan Name:</td>
								<td colSpan={3}> {insurance.type === IStatusAlertInsuranceType.QMB ? insurance.insuranceDescription : payer.name}</td>
							</tr>
							{insurance.type === IStatusAlertInsuranceType.Secondary && !(payer.insuranceTypeCodeDefinition === null || payer.insuranceTypeCodeDefinition === '') ? (
								<tr>
									<td>Insurance Type:</td>
									<td colSpan={3}>{payer.insuranceTypeCodeDefinition}</td>
								</tr>
							) : (
								<></>
							)}
							{!(payer.message === null || payer.message === '') ? (
								<tr>
									<td>Message:</td>
									<td colSpan={3}>{payer.message}</td>
								</tr>
							) : (
								<></>
							)}
							{this.getAddress(payer).length > 0 ? (
								<tr>
									<td>Address:</td>
									<td colSpan={3}>{this.getAddress(payer)}</td>
								</tr>
							) : (
								<></>
							)}
							{payer.telephone !== null ? (
								<tr>
									<td>Telephone:</td>
									<td colSpan={3}>
										<NumberFormat format="(###) ###-####" value={payer.telephone} displayType={'text'} />
									</td>
								</tr>
							) : (
								<></>
							)}
						</tbody>
					</table>
				)
			})
		})
	}

	getAddress(payer: IAdditionalPayer): string {
		let address = ''

		if (payer.address1) {
			address = payer.address1
		}
		if (payer.address2) address = address.length > 0 ? address + ' ' + payer.address2 : payer.address2

		if (payer.city) address = address.length > 0 ? address + ', ' + payer.city : payer.city

		if (payer.state) address = address.length > 0 ? address + ', ' + payer.state : payer.state

		if (payer.zip) address = address.length > 0 ? address + ' ' + this.FormatZip(payer.zip) : this.FormatZip(payer.zip)

		return address
	}

	FormatZip(zipValue: string): string {
		if (zipValue === '' || zipValue === null) return ''
		let format = zipValue
		if (format.indexOf('-') === -1 && format.length > 5) format = format.substring(0, 5) + '-' + format.substring(5)
		return format
	}
	getIdentifier(additionalIdentifiers: IAdditionalIdentifiers[]): string {
		let identifier = ''
		const contractNumber = additionalIdentifiers.some(i => i.identifierCode === '18')
		const pbpNumber = additionalIdentifiers.some(i => i.identifierCode === 'N6')

		if (contractNumber)
			identifier += additionalIdentifiers
				.filter(i => i.identifierCode === '18')
				.map(i => i.identifier)
				.slice(0, 1)
				.shift()

		if (contractNumber && pbpNumber) identifier += ' / '

		if (!contractNumber && pbpNumber) identifier += '-- / '

		if (contractNumber && !pbpNumber) identifier += ' / --'
		if (pbpNumber)
			identifier += additionalIdentifiers
				.filter(i => i.identifierCode === 'N6')
				.map(i => i.identifier)
				.slice(0, 1)
				.shift()
		return identifier
	}
	render(): React.ReactNode {
		const section = this.props.CoverageDiscoveryResponseStateManager.selectedMedicareStatusAlertSection
		if (!this.props.CoverageDiscoveryResponseStateManager.selectedMedicareStatusAlertSection) return <></>
		else {
			return (
				<div className="cdResponsePage container" style={{ padding: 0, margin: 0, width: '100%' }}>
					{section.statusAlerts && this.renderStatusAlert(section.statusAlerts)}
				</div>
			)
		}
	}
}
